// IM-XPR-002 (IM-UI-093, IM-UX-110) — Fluxo visual de macrofases conectadas. Reutilizável por qualquer objetivo.
// Acessível como abas: cada fase é uma aba (setas, Home e End navegam), o painel contextual é o tabpanel e a fase em que
// a pessoa está recebe aria-current="step". O estado nunca depende só da cor: há rótulo, ícone e texto para leitor de
// tela. Selecionar uma fase não navega: só troca o painel (a página continua a mesma).
import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { MACRO_STATE_LABEL, type MacroState, type PhaseStatusView } from '@intelimove/contracts';

export interface FlowPhase {
  key: string; name: string; question: string;
  /** `null` = situação ainda desconhecida (objetivo não iniciado ou conceitual). */
  state: MacroState | null;
  current: boolean;
  /** Quantidade relevante, em texto curto (ex.: "5 para classificar"). */
  count?: string | null;
  /** IM-XPR-005: estado PADRONIZADO de tela (Não iniciada, Disponível, Em andamento, Aguardando decisão, Bloqueada, Concluída). */
  status?: PhaseStatusView | null;
}

const GLYPH: Partial<Record<MacroState, string>> = { CONCLUIDA: '✓', CONTINUA: '↻', BLOQUEADA: '!', DISPENSADA: '–' };
const TONE: Record<MacroState, string> = {
  CONCLUIDA: 'done', CONTINUA: 'continuous', ATUAL: 'now', PRONTA: 'ready', EM_ANDAMENTO: 'working', AGUARDANDO_DECISAO: 'waiting', AGUARDANDO_REVISAO: 'waiting',
  AGUARDANDO_INFORMACAO: 'waiting', BLOQUEADA: 'blocked', AGUARDANDO_FASE_ANTERIOR: 'later', DISPENSADA: 'skipped',
};
/** Rótulo do selo: a fase atual diz "Você está aqui"; as demais, o estado. */
export function badgeOf(p: Pick<FlowPhase, 'state' | 'current' | 'status'>, unknownLabel = 'Ainda não começou'): string {
  const label = p.status ? p.status.label : p.state ? MACRO_STATE_LABEL[p.state] : null;
  if (p.current) return label && !(p.status ? p.status.status === 'EM_ANDAMENTO' && !p.status.detail : p.state === 'ATUAL') ? `Você está aqui · ${label}` : 'Você está aqui';
  return label ?? unknownLabel;
}

export function ObjectiveFlow({ id, label, phases, selected, onSelect, children, unknownLabel }: {
  id: string; label: string; phases: readonly FlowPhase[]; selected: string; onSelect: (key: string) => void; children: ReactNode; unknownLabel?: string;
}) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const idx = Math.max(0, phases.findIndex((p) => p.key === selected));
  const doneUntil = phases.reduce((n, p, i) => (p.state === 'CONCLUIDA' || p.state === 'DISPENSADA' || p.state === 'CONTINUA' ? i + 1 : n), 0);
  const move = (e: KeyboardEvent, i: number) => {
    const n = phases.length;
    const to = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? (i + 1) % n : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? (i - 1 + n) % n : e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : -1;
    if (to < 0) return;
    e.preventDefault();
    onSelect(phases[to]!.key);
    refs.current[to]?.focus();
  };
  return (
    <>
      <div className="xp-flow" role="tablist" aria-label={label} aria-orientation="horizontal" style={{ ['--xp-n' as string]: phases.length, ['--xp-done' as string]: `${phases.length > 1 ? Math.min(100, (Math.max(0, doneUntil - 0.5) / (phases.length - 1)) * 100) : 0}%` }}>
        {phases.map((p, i) => {
          const tone = p.state ? TONE[p.state] : 'later';
          const isSel = i === idx;
          const badge = badgeOf(p, unknownLabel);
          return (
            <button key={p.key} ref={(el) => { refs.current[i] = el; }} type="button" role="tab" id={`${id}-tab-${p.key}`} aria-controls={`${id}-panel`}
              aria-selected={isSel} tabIndex={isSel ? 0 : -1} aria-current={p.current ? 'step' : undefined}
              className={`xp-step tone-${tone}${p.current ? ' current' : ''}${isSel ? ' selected' : ''}`}
              onClick={() => onSelect(p.key)} onKeyDown={(e) => move(e, i)}>
              <span className="xp-node" aria-hidden="true">{(p.state && GLYPH[p.state]) ?? i + 1}</span>
              <span className="xp-step-text">
                <b>{p.name}</b>
                <small>{p.question}</small>
                <span className="xp-badge">{badge}</span>
                {p.count ? <span className="xp-count">{p.count}</span> : null}
              </span>
              <span className="sr-only">. Fase {i + 1} de {phases.length}.</span>
            </button>
          );
        })}
      </div>
      <section className="xp-workspace" role="tabpanel" id={`${id}-panel`} aria-labelledby={`${id}-tab-${phases[idx]?.key}`} tabIndex={-1}>
        {children}
      </section>
    </>
  );
}
